import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { DataTable } from "@/components/core/data-table";
import { useModal } from "@/components/core/modals/use-modal";
import { Content } from "@/components/layouts/content";
import { SimpleImageViewer } from "@/components/pdf-viewer/simple-image-viewer";
import { SimplePdfViewer } from "@/components/pdf-viewer/simple-pdf-viewer";
import { AttachmentTypeBadge } from "@/components/projects/attachment-type-badge";
import { UploadAttachmentModalContent } from "@/components/projects/upload-attachment-modal-content";
import { projectQueryOptions } from "@/features/dashboard/api/queries/project-query";
import { useSalesContractsByProjectQuery } from "@/features/dashboard/api/queries/sales-contract-query";
import type { Project } from "@/features/dashboard/types/project";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import { useProjectCategoryAttachments } from "@/hooks/use-enums";
import {
	useDeleteProjectAttachmentMutation,
	useDownloadProjectAttachmentMutation,
	useProjectAttachmentsByProjectQuery,
	useUpdateProjectAttachmentMutation,
} from "@/hooks/use-project-attachments";
import type { ProjectAttachment } from "@/types/project-attachment";
import { ProjectCategoryAttachment } from "@/types/project-category-attachment";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import {
	IconBuilding,
	IconCalendar,
	IconCalendarEvent,
	IconCheck,
	IconChevronDown,
	IconChevronRight,
	IconClock,
	IconContract,
	IconCurrencyPeso,
	IconDownload,
	IconEdit,
	IconEye,
	IconFile,
	IconMapPin,
	IconPaperclip,
	IconPlus,
	IconTrash,
	IconUsers,
	IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/projects_/$projectId")({
	component: Page,
});

function Page() {
	const { projectId } = Route.useParams();

	const projectQuery = useQuery(projectQueryOptions(projectId));
	const contractsQuery = useSalesContractsByProjectQuery(projectId);

	const project = projectQuery.data;
	const contracts = contractsQuery.data?.data || [];

	if (projectQuery.isLoading) {
		return (
			<Content pageName="Project Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading project details...</p>
					</div>
				</div>
			</Content>
		);
	}

	if (!project) {
		return (
			<Content pageName="Project Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<p className="text-lg text-gray-600">Project not found</p>
					</div>
				</div>
			</Content>
		);
	}

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	// Calculate contract statistics
	const contractStats = {
		total: contracts.length,
		active: contracts.filter((c) => c.status === "active").length,
		expired: contracts.filter((c) => c.status === "expired").length,
		totalValue: contracts.reduce((sum, c) => sum + (c.contract_amount || 0), 0),
		monthlyRecurring: contracts.reduce((sum, c) => sum + (c.monthly_recurring_sales || 0), 0),
	};

	return (
		<Content pageName={`Project: ${project.name}`}>
			<div className="p-6 space-y-6">
				<div className="flex items-start justify-between">
					<div>
						<h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
						<p className="text-sm text-gray-500 mt-1">{project.description || "No description"}</p>
					</div>
					<div className="flex items-center gap-2">
						<Badge variant={project.status === "active" ? "green" : project.status === "completed" ? "blue" : "gray"}>
							{project.status?.charAt(0).toUpperCase()}
							{project.status?.slice(1)}
						</Badge>
						<Badge color={project.category === "recurring" ? "green" : project.category === "recurring-one-time" ? "purple" : "blue"} size="xs">
							{project.category === "recurring" ? "Recurring" : project.category === "recurring-one-time" ? "Recurring & One-time" : "One-time"}
						</Badge>
					</div>
				</div>

				{/* Enhanced info cards with contract statistics */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
					<InfoCard icon={<IconBuilding className="w-5 h-5" />} label="Client" value={project.client?.company || project.client?.name || "Not specified"} />
					<InfoCard icon={<IconCalendar className="w-5 h-5" />} label="Start Date" value={project.date_start ? formatDate(project.date_start) : "Not set"} />
					<InfoCard icon={<IconContract className="w-5 h-5" />} label="Contracts" value={`${contractStats.total} (${contractStats.active} active)`} />
					<InfoCard icon={<IconCurrencyPeso className="w-5 h-5" />} label="Total Value" value={formatCurrency(contractStats.totalValue)} />
					<InfoCard icon={<IconClock className="w-5 h-5" />} label="Monthly Recurring" value={formatCurrency(contractStats.monthlyRecurring)} />
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Enhanced Project Information */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Project Information</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Project Name" value={project.name} />
								<DetailItem
									label="Category"
									value={
										project.category === "recurring"
											? "Recurring Project"
											: project.category === "recurring-one-time"
												? "Recurring & One-time Project"
												: "One-time Project"
									}
								/>
								<DetailItem label="Client" value={project.client?.company || project.client?.name || "-"} />
								<DetailItem label="Client Email" value={project.client?.email || "-"} />
								<DetailItem label="Description" value={project.description || "-"} />
								<DetailItem label="Status" value={project.status || "-"} />
								<DetailItem label="Start Date" value={project.date_start ? formatDate(project.date_start) : "-"} />
							</div>
						</CardContent>
					</Card>

					{/* Statistics & Timeline */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Timeline & Statistics</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Created" value={project.created_at ? formatDate(project.created_at) : "-"} />
								<DetailItem label="Last Updated" value={project.updated_at ? formatDate(project.updated_at) : "-"} />
								<DetailItem label="Total Contracts" value={contractStats.total.toString()} />
								<DetailItem label="Active Contracts" value={contractStats.active.toString()} />
								<DetailItem label="Expired Contracts" value={contractStats.expired.toString()} />
							</div>
						</CardContent>
					</Card>

					{/* Financial Summary */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Financial Summary</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Total Contract Value" value={formatCurrency(contractStats.totalValue)} />
								<DetailItem label="Monthly Recurring Revenue" value={formatCurrency(contractStats.monthlyRecurring)} />
								<DetailItem
									label="Average Contract Value"
									value={contractStats.total > 0 ? formatCurrency(contractStats.totalValue / contractStats.total) : "-"}
								/>
								<DetailItem
									label="Project Type"
									value={
										project.category === "recurring"
											? "Recurring Revenue"
											: project.category === "recurring-one-time"
												? "Recurring & One-time Revenue"
												: "One-time Payment"
									}
								/>
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Sales Contracts Section */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconContract className="w-5 h-5" />
							Sales Contracts ({contracts.length})
						</CardTitle>
					</CardHeader>
					<CardContent>
						{contractsQuery.isLoading ? (
							<div className="flex items-center justify-center h-32">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
								<span className="ml-2 text-sm text-gray-600">Loading contracts...</span>
							</div>
						) : contracts.length === 0 ? (
							<div className="text-center py-8 text-gray-500">
								<IconContract className="w-12 h-12 mx-auto mb-2 text-gray-300" />
								<p>No sales contracts found for this project.</p>
							</div>
						) : (
							<div className="space-y-4">
								{contracts.map((contract) => (
									<ContractCard key={contract.id} contract={contract} />
								))}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Project Attachments Section */}
				<ProjectAttachmentsSection projectId={projectId} projectCategory={project.category} />
			</div>
		</Content>
	);
}

function ProjectAttachmentsSection({ projectId, projectCategory }: { projectId: string; projectCategory: string }) {
	const { data: attachmentTypes } = useProjectCategoryAttachments();
	const { data: attachmentsData, isLoading, refetch } = useProjectAttachmentsByProjectQuery(projectId);
	const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
	const modal = useModal();

	const attachments = attachmentsData?.data || [];

	const toggleSection = (type: string) => {
		const newExpanded = new Set(expandedSections);
		if (newExpanded.has(type)) {
			newExpanded.delete(type);
		} else {
			newExpanded.add(type);
		}
		setExpandedSections(newExpanded);
	};

	// Group real attachments by type
	const attachmentGroups =
		attachmentTypes
			?.map((type) => ({
				type: type.value,
				info: type,
				attachments: attachments.filter((att) => att.type === type.value),
			}))
			.filter((group) => group.attachments.length > 0) || [];

	const totalAttachments = attachments.length;

	const handleUploadSuccess = () => {
		refetch();
	};

	const handleUpload = () => {
		modal.openModal({
			title: "Upload Attachment",
			description: "Upload a new document for this project",
			size: "md",
			component: <UploadAttachmentModalContent projectId={projectId} onSuccess={handleUploadSuccess} />,
		});
	};

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="flex items-center gap-2">
							<IconPaperclip className="w-5 h-5" />
							Project Attachments ({totalAttachments})
						</CardTitle>
						<p className="text-sm text-gray-600 mt-1">
							Documents organized by attachment type for this{" "}
							{projectCategory === "recurring" ? "recurring" : projectCategory === "recurring-one-time" ? "recurring & one-time" : "one-time"} project
						</p>
					</div>
					<Button onClick={handleUpload} size="sm" className="flex items-center gap-2">
						<IconPlus className="w-4 h-4" />
						Add
					</Button>
				</div>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
						<span className="ml-2 text-sm text-gray-600">Loading attachments...</span>
					</div>
				) : totalAttachments === 0 ? (
					<div className="text-center py-8 text-gray-500">
						<IconPaperclip className="w-12 h-12 mx-auto mb-2 text-gray-300" />
						<p className="font-medium">No attachments uploaded yet</p>
						<p className="text-sm">Upload documents to organize them by type</p>
						<Button onClick={handleUpload} variant="outline" className="mt-4">
							<IconPlus className="w-4 h-4 mr-2" />
							Upload First Attachment
						</Button>
					</div>
				) : (
					<div className="space-y-4">
						{attachmentGroups.map((group) => (
							<AttachmentGroup
								key={group.type}
								group={group}
								isExpanded={expandedSections.has(group.type)}
								onToggle={() => toggleSection(group.type)}
								projectId={projectId}
								onUpdate={handleUploadSuccess}
							/>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

interface AttachmentGroupProps {
	group: {
		type: string;
		info: Record<string, unknown>;
		attachments: ProjectAttachment[];
	};
	isExpanded: boolean;
	onToggle: () => void;
	projectId: string;
	onUpdate: () => void;
}

function AttachmentGroup({ group, isExpanded, onToggle, projectId, onUpdate }: AttachmentGroupProps) {
	return (
		<div className="border border-gray-200 rounded-lg">
			<button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
				<div className="flex items-center gap-3">
					<div className="flex items-center gap-2">
						{isExpanded ? <IconChevronDown className="w-4 h-4 text-gray-500" /> : <IconChevronRight className="w-4 h-4 text-gray-500" />}
						<AttachmentTypeBadge type={group.type} size="sm" />
					</div>
					<div className="text-left">
						<h4 className="font-medium text-gray-900">{group.info.label}</h4>
						<p className="text-sm text-gray-500">{group.info.description}</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full font-medium">
						{group.attachments.length} {group.attachments.length === 1 ? "file" : "files"}
					</span>
				</div>
			</button>

			{isExpanded && (
				<div className="border-t border-gray-100">
					<div className="p-4 pt-0">
						<div className="space-y-2 mt-4">
							{group.attachments.map((attachment) => (
								<AttachmentItem key={attachment.id} attachment={attachment} onUpdate={onUpdate} />
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

function AttachmentItem({ attachment, onUpdate }: { attachment: ProjectAttachment; onUpdate: () => void }) {
	const downloadMutation = useDownloadProjectAttachmentMutation();
	const deleteMutation = useDeleteProjectAttachmentMutation();
	const updateMutation = useUpdateProjectAttachmentMutation();
	const modal = useModal();
	const [isEditingDate, setIsEditingDate] = useState(false);
	const [editDateValue, setEditDateValue] = useState(attachment.document_date || "");

	const getFileIcon = (filename: string) => {
		const ext = filename.split(".").pop()?.toLowerCase();
		if (ext === "pdf") return "📄";
		if (["doc", "docx"].includes(ext || "")) return "📝";
		if (["xls", "xlsx"].includes(ext || "")) return "📊";
		if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext || "")) return "🖼️";
		return "📎";
	};

	const handleDownload = async () => {
		try {
			await downloadMutation.mutateAsync({
				id: attachment.id,
				filename: attachment.original_filename,
			});
		} catch (error) {
			console.error("Download failed:", error);
			toast.error("Failed to download attachment");
		}
	};

	const handlePreview = () => {
		if (attachment.is_pdf || attachment.mime_type === "application/pdf") {
			modal.openModal({
				title: `Preview - ${attachment.original_filename}`,
				size: "xl",
				component: <SimplePdfViewer fileUrl={attachment.file_url} fileName={attachment.original_filename} onDownload={handleDownload} />,
			});
		} else if (attachment.is_image || attachment.mime_type?.startsWith("image/")) {
			modal.openModal({
				title: `Preview - ${attachment.original_filename}`,
				size: "xl",
				component: <SimpleImageViewer imageUrl={attachment.file_url} fileName={attachment.original_filename} onDownload={handleDownload} />,
			});
		}
	};

	const handleDelete = () => {
		modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Delete Attachment",
			description: `Are you sure you want to delete "${attachment.original_filename}"? This action cannot be undone.`,
			onConfirm: async () => {
				try {
					await deleteMutation.mutateAsync(attachment.id);
					toast.success("Attachment deleted successfully");
					onUpdate();
				} catch (error) {
					console.error("Delete failed:", error);
					toast.error("Failed to delete attachment");
				}
			},
		});
	};

	const handleSaveDate = async () => {
		try {
			await updateMutation.mutateAsync({
				id: attachment.id,
				data: { document_date: editDateValue || null },
			});
			toast.success("Document date updated");
			setIsEditingDate(false);
			onUpdate();
		} catch (error) {
			console.error("Update failed:", error);
			toast.error("Failed to update document date");
		}
	};

	const handleCancelEditDate = () => {
		setEditDateValue(attachment.document_date || "");
		setIsEditingDate(false);
	};

	return (
		<div className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
			<div className="flex items-center gap-3 min-w-0 flex-1">
				<div className="text-lg flex-shrink-0">{getFileIcon(attachment.original_filename)}</div>
				<div className="min-w-0 flex-1">
					<p className="font-medium text-gray-900 truncate">{attachment.original_filename}</p>
					<div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
						<span>{attachment.human_file_size}</span>
						<span>•</span>
						<span>Uploaded {formatDate(attachment.uploaded_at)}</span>
						<span>•</span>
						<span>by {attachment.uploaded_by_user?.full_name || "Unknown"}</span>
						{attachment.description && (
							<>
								<span>•</span>
								<span className="truncate max-w-[200px]" title={attachment.description}>
									{attachment.description}
								</span>
							</>
						)}
					</div>
					<div className="flex items-center gap-1 mt-1">
						<IconCalendarEvent className="w-3 h-3 text-gray-400" />
						{isEditingDate ? (
							<div className="flex items-center gap-1">
								<input
									type="date"
									value={editDateValue}
									onChange={(e) => setEditDateValue(e.target.value)}
									className="text-xs border border-gray-300 rounded px-1.5 py-0.5"
								/>
								<button
									type="button"
									onClick={handleSaveDate}
									disabled={updateMutation.isPending}
									className="text-green-600 hover:text-green-700 p-0.5"
									title="Save"
								>
									<IconCheck className="w-3.5 h-3.5" />
								</button>
								<button type="button" onClick={handleCancelEditDate} className="text-gray-400 hover:text-gray-600 p-0.5" title="Cancel">
									<IconX className="w-3.5 h-3.5" />
								</button>
							</div>
						) : (
							<button
								type="button"
								onClick={() => setIsEditingDate(true)}
								className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors"
								title="Edit document date"
							>
								<span>{attachment.document_date ? formatDate(attachment.document_date, "PP") : "No date"}</span>
								<IconEdit className="w-3 h-3" />
							</button>
						)}
					</div>
				</div>
			</div>
			<div className="flex items-center gap-2 flex-shrink-0">
				{(attachment.is_pdf || attachment.mime_type === "application/pdf" || attachment.is_image || attachment.mime_type?.startsWith("image/")) && (
					<Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={handlePreview} title="Preview PDF">
						<IconEye className="w-3 h-3" />
					</Button>
				)}
				<Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={handleDownload} disabled={downloadMutation.isPending} title="Download attachment">
					<IconDownload className="w-3 h-3" />
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
					onClick={handleDelete}
					disabled={deleteMutation.isPending}
					title="Delete attachment"
				>
					<IconTrash className="w-3 h-3" />
				</Button>
			</div>
		</div>
	);
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
	return (
		<Card>
			<CardContent className="p-4">
				<div className="flex items-center gap-3">
					<div className="text-gray-500">{icon}</div>
					<div>
						<p className="text-xs text-gray-500">{label}</p>
						<p className="text-sm font-semibold text-gray-900">{value}</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function DetailItem({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-xs text-gray-500 font-medium">{label}</p>
			<p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
		</div>
	);
}

function ContractCard({ contract }: { contract: SalesContract }) {
	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	return (
		<div className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
			<div className="flex items-start justify-between mb-3">
				<div className="flex items-center gap-2">
					<h4 className="font-medium text-gray-900">Contract #{contract.id.slice(-8)}</h4>
					<Badge variant={contract.status === "active" ? "green" : contract.status === "expired" ? "red" : "gray"}>
						{contract.status?.charAt(0).toUpperCase()}
						{contract.status?.slice(1)}
					</Badge>
					{contract.is_extended && <Badge variant="blue">Extended</Badge>}
				</div>
				<div className="text-right">
					<p className="text-sm font-semibold text-gray-900">{formatCurrency(contract.contract_amount)}</p>
					{contract.monthly_recurring_sales > 0 && <p className="text-xs text-gray-500">{formatCurrency(contract.monthly_recurring_sales)}/mo</p>}
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
				<div>
					<p className="text-gray-500 flex items-center gap-1">
						<IconCalendar className="w-4 h-4" />
						Start Date
					</p>
					<p className="font-medium">{formatDate(contract.start_date)}</p>
				</div>
				<div>
					<p className="text-gray-500 flex items-center gap-1">
						<IconCalendar className="w-4 h-4" />
						End Date
					</p>
					<p className="font-medium">{formatDate(contract.effective_end_date || contract.end_date)}</p>
				</div>
				<div>
					<p className="text-gray-500 flex items-center gap-1">
						<IconUsers className="w-4 h-4" />
						Salesperson
					</p>
					<p className="font-medium">{contract.salesperson?.name || "Not assigned"}</p>
				</div>
			</div>

			{contract.payment_type && (
				<div className="mt-3 pt-3 border-t border-gray-100">
					<span className="text-xs text-gray-500">Payment Type: </span>
					<span className="text-xs font-medium text-gray-700">
						{contract.payment_type.charAt(0).toUpperCase()}
						{contract.payment_type.slice(1)}
					</span>
				</div>
			)}

			{contract.notes && (
				<div className="mt-3 pt-3 border-t border-gray-100">
					<p className="text-xs text-gray-500 mb-1">Notes:</p>
					<p className="text-sm text-gray-700">{contract.notes}</p>
				</div>
			)}
		</div>
	);
}
